import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { copilotApi } from '../services/copilotApi';
import { SubscriptionChoice } from '../components/SubscriptionChoice';

/**
 * Subscription interfaces - matching backend definitions
 */
export interface Subscription {
  valid_from: string; // ISO date string
  valid_until: string | null; // ISO date string or null for unlimited access
  subscription_title: string; // e.g., "Early Access", "Access Code", "Starter"
  subscription_type: 'early_access' | 'access_code' | 'trial' | 'premium' | 'enterprise';
  veterinarian_id: number;
  // 'past_due' is a grace state: a payment failed but Stripe is still retrying,
  // so access is kept while we prompt the user to update their card.
  status: 'active' | 'past_due' | 'expired' | 'trial' | 'cancelled' | 'pending';
  created_at: string;
  updated_at: string;
}

export interface SubscriptionResponse {
  subscription: Subscription | null;
  is_valid: boolean;
  expires_in_days?: number | null; // null for unlimited subscriptions
}

export interface SubscriptionContextValue {
  subscription: Subscription | null;
  loading: boolean;
  error: string | null;
  refreshSubscription: () => Promise<void>;
  isSubscriptionValid: boolean;
  /** Redeem an access code; throws on failure so the UI can show the reason. */
  redeemAccessCode: (code: string) => Promise<void>;
  /** Start a Stripe Checkout trial; redirects the browser to Stripe. */
  startCheckout: () => Promise<void>;
  /** Open the Stripe billing portal (update card, change/cancel plan, invoices). */
  openBillingPortal: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextValue | undefined>(undefined);

/**
 * Hook to use subscription context
 */
export const useSubscription = (): SubscriptionContextValue => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

/**
 * Client-side subscription validation logic
 */
function isSubscriptionCurrentlyValid(subscription: Subscription | null): boolean {
  if (!subscription) {
    return false;
  }

  // Grace period: payment failed but Stripe is still retrying. Keep access
  // (the payment-failed banner prompts the user to update their card).
  if (subscription.status === 'past_due') {
    return true;
  }

  if (subscription.status !== 'active') {
    return false;
  }

  const now = new Date();
  const validFrom = new Date(subscription.valid_from);
  
  // Check valid_from date
  if (now < validFrom) {
    return false;
  }
  
  // If valid_until is null (early_access), subscription is unlimited
  if (subscription.valid_until === null) {
    return true;
  }
  
  // Otherwise check expiry date
  const validUntil = new Date(subscription.valid_until);
  return now <= validUntil;
}

interface SubscriptionProviderProps {
  children: ReactNode;
}

/**
 * Subscription Provider Component
 * Only renders children when user has a valid subscription
 * Shows SubscriptionChoice when no subscription exists
 */
export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const fetchSubscription = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await copilotApi.get<SubscriptionResponse>('/subscription');
      setSubscription(response.subscription);
      
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
      setError('Failed to load subscription information');
    } finally {
      setLoading(false);
    }
  }, []);
  
  const redeemAccessCode = useCallback(async (code: string) => {
    setError(null);
    const response = await copilotApi.post<SubscriptionResponse>('/subscription/access-code', { code });
    setSubscription(response.subscription);
  }, []);

  const startCheckout = useCallback(async () => {
    setError(null);
    const response = await copilotApi.post<{ url: string }>('/subscription/checkout');
    if (response.url) {
      window.location.href = response.url;
    } else {
      throw new Error('No checkout URL returned');
    }
  }, []);

  const openBillingPortal = useCallback(async () => {
    setError(null);
    const response = await copilotApi.post<{ url: string }>('/subscription/portal');
    if (response.url) {
      window.location.href = response.url;
    } else {
      throw new Error('No billing portal URL returned');
    }
  }, []);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // After returning from Stripe Checkout (?subscription=success), refresh and
  // clean the query param. The webhook may lag, so fetchSubscription re-reads.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('subscription')) {
      const result = params.get('subscription');
      params.delete('subscription');
      const newSearch = params.toString();
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`
      );
      if (result === 'success') {
        fetchSubscription();
      }
    }
  }, [fetchSubscription]);

  const value: SubscriptionContextValue = {
    subscription,
    loading,
    error,
    refreshSubscription: fetchSubscription,
    redeemAccessCode,
    startCheckout,
    openBillingPortal,
    isSubscriptionValid: isSubscriptionCurrentlyValid(subscription)
  };
  
  // Show loading state
  if (loading) {
    return (
      <SubscriptionContext.Provider value={value}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          width: '100vw',
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: 1000,
          backgroundColor: 'transparent'
        }}>
          Loading your access...
        </div>
      </SubscriptionContext.Provider>
    );
  }
  
  // Show the subscription choice screen if no subscription exists
  if (!subscription) {
    return (
      <SubscriptionContext.Provider value={value}>
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 1000,
          backgroundColor: 'transparent'
        }}>
          <SubscriptionChoice />
        </div>
      </SubscriptionContext.Provider>
    );
  }
  
  // Subscription exists but is no longer valid (expired access code, expired or
  // cancelled Stripe subscription). Route back to the choice screen so the user
  // can redeem a new code or start a subscription instead of hitting a dead end.
  if (!isSubscriptionCurrentlyValid(subscription)) {
    return (
      <SubscriptionContext.Provider value={value}>
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 1000,
          backgroundColor: 'transparent'
        }}>
          <SubscriptionChoice heading="Your access has ended" />
        </div>
      </SubscriptionContext.Provider>
    );
  }
  
  // Render children for valid subscriptions
  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
